import { useMemo, useState } from 'react';
import emailjs from '@emailjs/browser';
import { useTranslation } from '../../i18n/TranslationContext';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CreatorCard from './components/CreatorCard';
import { creators } from '../../mocks/creators';
import { filterCategories, joinCategories } from '../../mocks/categories';

const emptyJoinForm = {
  name: '',
  email: '',
  phone: '',
  category: '',
  message: '',
};

type JoinStatus = 'idle' | 'sending' | 'success' | 'error';
type JoinFormValues = typeof emptyJoinForm;

export default function CreatorsPage() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [joinStatus, setJoinStatus] = useState<JoinStatus>('idle');
  const [joinError, setJoinError] = useState('');
  const [joinForm, setJoinForm] = useState<JoinFormValues>(emptyJoinForm);

  const filteredCreators = useMemo(() => {
    if (activeCategory === 'all') return creators;
    return creators.filter((creator) => creator.categories.includes(activeCategory as any));
  }, [activeCategory]);

  const getCreatorName = (creator: typeof creators[number]) => {
    if (i18n.language === 'ko') return creator.nameKo;
    if (i18n.language === 'zh') return creator.nameZh;
    return creator.name;
  };

  const getCreatorDesc = (creator: typeof creators[number]) => {
    if (i18n.language === 'ko') return creator.descriptionKo;
    if (i18n.language === 'zh') return creator.descriptionZh;
    return creator.description;
  };

  const handleJoinChange = (field: keyof JoinFormValues, value: string) => {
    setJoinForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetJoinForm = (form: HTMLFormElement) => {
    setJoinForm(emptyJoinForm);
    form.reset();
  };

  const handleJoinSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formPayload = new FormData(form);
    const honeypot = String(formPayload.get('phone_alt') || '').trim();

    if (honeypot) {
      setJoinStatus('success');
      resetJoinForm(form);
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setJoinStatus('error');
      setJoinError(t('contact.error', 'Email settings are missing. Please check the EmailJS environment variables.'));
      return;
    }

    try {
      setJoinStatus('sending');
      setJoinError('');
      await emailjs.sendForm(serviceId, templateId, form, publicKey);
      setJoinStatus('success');
      resetJoinForm(form);
    } catch (error) {
      console.error('EmailJS creator application failed:', error);
      setJoinStatus('error');
      setJoinError(t('contact.error', 'Failed to send. Please try again.'));
    }
  };

  return (
    <>
      <title>Dragonfruit Creators | Join Our Global Creator Network</title>
      <meta
        name="description"
        content="Meet Dragonfruit creators across beauty, fashion, lifestyle, food, TikTok, Xiaohongshu, and global platforms."
      />
      <main className="min-h-screen bg-background-50">
        <Navbar />

        <section className="pt-28 pb-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs font-medium uppercase tracking-widest text-primary-500 mb-4">
                {t('nav.creators')}
              </p>
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-5">
                {t('creators.heading')}
              </h1>
              <p className="max-w-2xl mx-auto text-sm md:text-base text-foreground-600 leading-relaxed">
                {t('creators.subtitle')}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
              {filterCategories.map((category) => (
                <button
                  key={category.value}
                  onClick={() => setActiveCategory(category.value)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === category.value
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-foreground-50 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  {t(category.key)}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {filteredCreators.map((creator) => (
                <CreatorCard
                  key={creator.id}
                  creator={creator}
                  getName={getCreatorName}
                  getDesc={getCreatorDesc}
                />
              ))}
            </div>

            {filteredCreators.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-sm">{t('creators.no_results')}</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-20 md:py-28 bg-primary-50/30">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <span className="text-xs text-primary-500 font-medium uppercase tracking-widest mb-4 block">
              {t('creators.join_tagline')}
            </span>
            <h2 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">
              {t('creators.join_title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-10 max-w-xl mx-auto">
              {t('creators.join_desc')}
            </p>

            <form
              onSubmit={handleJoinSubmit}
              className="flex flex-col gap-4 text-left max-w-lg mx-auto"
            >
              <div>
                <label htmlFor="join-name" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('creators.join_name')}
                </label>
                <input
                  id="join-name"
                  type="text"
                  name="name"
                  value={joinForm.name}
                  onChange={(e) => handleJoinChange('name', e.target.value)}
                  required
                  placeholder={t('creators.join_name_placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="join-email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('creators.join_email')}
                </label>
                <input
                  id="join-email"
                  type="email"
                  name="email"
                  value={joinForm.email}
                  onChange={(e) => handleJoinChange('email', e.target.value)}
                  required
                  placeholder={t('creators.join_email_placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="join-phone" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('creators.join_phone')}
                </label>
                <input
                  id="join-phone"
                  type="tel"
                  name="phone_number"
                  value={joinForm.phone}
                  onChange={(e) => handleJoinChange('phone', e.target.value)}
                  required
                  placeholder={t('creators.join_phone_placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="join-category" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('creators.join_category')}
                </label>
                <select
                  id="join-category"
                  name="service"
                  value={joinForm.category}
                  onChange={(e) => handleJoinChange('category', e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  {joinCategories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {t(category.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="join-message" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('creators.join_message')}
                </label>
                <textarea
                  id="join-message"
                  name="message"
                  value={joinForm.message}
                  onChange={(e) => handleJoinChange('message', e.target.value)}
                  required
                  rows={4}
                  maxLength={500}
                  placeholder={t('creators.join_message_placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                />
                <p className="text-xs text-foreground-400 mt-1 text-right">
                  {joinForm.message.length}/500
                </p>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <button
                type="submit"
                disabled={joinStatus === 'sending'}
                className="px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 disabled:bg-foreground-300 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 mt-2"
              >
                {joinStatus === 'sending' ? t('contact.sending', 'Sending...') : t('creators.join_send')}
                <i className="ri-send-plane-line"></i>
              </button>

              {joinStatus === 'success' && (
                <p className="text-accent-600 text-sm font-medium text-center">{t('creators.join_success')}</p>
              )}
              {joinStatus === 'error' && (
                <p className="text-primary-500 text-sm text-center">{joinError}</p>
              )}
            </form>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}
